import { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { setCurrentTab, showSideBar, showNavBar } from '../../actions/commonActions'
import { getCoaches, inviteCoach } from '../../actions/coachActions'
import { getAllOrganizations } from '../../actions/organizationsActions'
import { START_PAGE_DEFAULT, PER_PAGE_DEFAULT } from '../../misc/Constants'
import { debounce } from '../../utility/debounceHelper'

import Loader from '../common/loader/Loader'
import TablePlaceholder from '../common/TablePlaceholder'
import InviteCoachModal from './InviteCoachModal'
import PageHeader from '../common/PageHeader/PageHeader'
import CoachesHeader from './CoachesHeader'
import CoachesFiltersPopover, { INITIAL_COACH_FILTERS, countCoachFilters } from './CoachesFiltersPopover'
import CoachesTable from './CoachesTable'
import CoachDrawer from './CoachDrawer'

import classes from './CoachesPage.module.scss'

const CoachesPage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const coaches = useSelector(state => state.coaches)
  const organizations = useSelector(state => state.organizations.organizations)

  const [searchText, setSearchText] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [coachLoader, setCoachLoader] = useState(true)
  const [isSearching, setIsSearching] = useState(false)
  const [isSortLoading, setIsSortLoading] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const organizationFilter = searchParams.get('organization_id') || ''
  const [filters, setFilters] = useState(INITIAL_COACH_FILTERS)
  const [sortOption, setSortOption] = useState({ sortField: null, sortValue: null })
  const [isFiltersPopoverOpen, setIsFiltersPopoverOpen] = useState(false)
  const [inspectedCoachId, setInspectedCoachId] = useState(null)
  const [showInviteCoachModal, setShowInviteCoachModal] = useState(false)

  const coachList = coaches.coaches || []
  const pagination = coaches.pagination || {}
  const selectedOrganization = organizations.find(o => String(o.id) === organizationFilter)
  const inspectedCoach = coachList.find(coach => coach.id === inspectedCoachId)
  const coachCount = pagination.total_count || coachList.length
  const hasMore = pagination.page < pagination.total_pages

  const fetchCoachesData = useCallback(({ page = START_PAGE_DEFAULT, append = false } = {}) => {
    const requestFilters = {
      status: filters.status === 'all' ? null : filters.status,
      withoutCommunity: filters.withoutCommunity,
      organizationId: organizationFilter || null,
      sortField: sortOption.sortValue ? sortOption.sortField : null,
      sortValue: sortOption.sortValue,
      append,
    }

    setIsSearching(true)
    dispatch(getCoaches(searchTerm, page, PER_PAGE_DEFAULT, requestFilters))
      .catch((error) => {
        if (!axios.isCancel(error)) {
          toast.error(t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT })
        }
      })
      .finally(() => {
        setCoachLoader(false)
        setIsSearching(false)
        setIsSortLoading(false)
      })
  }, [dispatch, t, searchTerm, filters, organizationFilter, sortOption])

  useEffect(() => {
    dispatch(setCurrentTab('Coaches'))
    dispatch(showSideBar(true))
    dispatch(showNavBar(true))
    dispatch(getAllOrganizations())
  }, [dispatch])

  useEffect(() => {
    fetchCoachesData()
  }, [fetchCoachesData])

  const handleOrganizationChange = (organizationId) => {
    setSearchParams(organizationId ? { organization_id: organizationId } : {})
  }

  const debouncedSearch = useMemo(() => debounce((search) => setSearchTerm(search)), [])

  const handleSearchChange = (e) => {
    setSearchText(e.target.value)
    debouncedSearch(e.target.value)
  }

  const handleSort = (sortField, sortValue) => {
    setIsSortLoading(true)
    setSortOption({ sortField, sortValue })
  }

  const fetchMoreCoaches = () => {
    fetchCoachesData({ page: pagination.page + 1, append: true })
  }

  const handleInviteCoach = (inviteData) => (
    dispatch(inviteCoach(inviteData))
      .then(() => {
        setShowInviteCoachModal(false)
        toast.success(t('coaches.page.inviteSuccess'), { position: toast.POSITION.TOP_RIGHT })
      })
      .catch((error) => {
        if (error.response?.status === 503) {
          toast.error(t('coaches.page.inviteUnavailable'), { position: toast.POSITION.TOP_RIGHT })
        } else {
          const errorMessage = error.response.data.errors[Object.keys(error.response.data.errors)[0]][0] ?? t('coaches.common.somethingWentWrong')
          toast.error(errorMessage, { position: toast.POSITION.TOP_RIGHT })
        }
      })
  )

  const inviteCoachButton = (
    <button className='btn btn-primary text-white pull-right mr-2' onClick={() => setShowInviteCoachModal(true)}>
      {t('coaches.page.inviteCoach')}
    </button>
  )

  return (
    <div id='coachesPage'>
      <PageHeader title={t('coaches.page.title')} actionComponent={inviteCoachButton} />

      <section className={classNames(classes.coachesList, 'd-flex flex-column')}>
        <CoachesHeader
          searchText={searchText}
          organizationFilter={organizationFilter}
          activeFilterCount={countCoachFilters(filters)}
          isFiltersOpen={isFiltersPopoverOpen}
          onSearchChange={handleSearchChange}
          onOrganizationChange={handleOrganizationChange}
          onFiltersClick={() => setIsFiltersPopoverOpen(prev => !prev)}
        />

        {selectedOrganization && (
          <div className={classNames(classes.organizationNote, 'd-flex align-items-center')}>
            <i className='fa fa-users' aria-hidden='true' />
            <span>
              <b>{selectedOrganization.name}</b>
              <span className={classes.organizationNoteStats}>
                {` · ${t('coaches.header.organizationCoachesNote', { count: selectedOrganization.coachesCount })}`}
                {` · ${t('coaches.header.organizationCertifiedNote', { count: selectedOrganization.certifiedCoachesCount })}`}
              </span>
            </span>
          </div>
        )}

        <main className={classNames(classes.wrapper, 'd-flex flex-column')}>
          {coachLoader ? (
            <div className='h-100 d-flex align-items-center justify-content-center'>
              <Loader>{t('coaches.page.loading')}</Loader>
            </div>
          ) : coachList.length === 0 ? (
            <TablePlaceholder message={t('coaches.table.noCoachesFound')} />
          ) : (
            <CoachesTable
              coachList={coachList}
              coachCount={coachCount}
              hasMore={hasMore}
              isLoading={isSearching}
              isSortLoading={isSortLoading}
              sortOption={sortOption}
              onSort={handleSort}
              onFetchMore={fetchMoreCoaches}
              onRowClick={(coach) => setInspectedCoachId(coach.id)}
              selectedCoachId={inspectedCoachId}
            />
          )}
        </main>
      </section>

      <CoachesFiltersPopover
        isOpen={isFiltersPopoverOpen}
        toggle={() => setIsFiltersPopoverOpen(prev => !prev)}
        currentFilters={filters}
        onApply={setFilters}
      />

      {inspectedCoach && <CoachDrawer coach={inspectedCoach} onClose={() => setInspectedCoachId(null)} />}

      {showInviteCoachModal && (
        <InviteCoachModal
          isOpen={showInviteCoachModal}
          onClose={() => setShowInviteCoachModal(false)}
          onSubmit={handleInviteCoach}
        />
      )}
    </div>
  )
}

export default CoachesPage
